import React from "react";
import { 
  Palette, Phone, MapPin, Trash2, CheckCircle, Menu, Plus, Minus, 
  Settings, LogOut, LayoutDashboard, Database, MessageSquare, 
  RotateCcw, X, Mail, Info, Factory, Globe, Home, Store, Users, Filter, List, Search, ClipboardList, ChevronDown
} from "lucide-react";
import { InquiryItem, SiteSettings } from "../types";

interface HeaderProps {
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  settings: SiteSettings;
  quoteCart: InquiryItem[];
  removeFromCart: (id: string) => void;
  updateCartItemNote: (id: string, note: string) => void;
  updateCartItemQuantity: (id: string, quantity: number) => void;
  onSubmitQuote: (customerData: { name: string; phone: string; company: string; note: string }) => void;
  inquirySuccess: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isAuthenticated: boolean;
  onLogout: () => void;
  selectedCategory: string;
  setSelectedCategory: (cat: string) => void;
  selectedOrigin: string;
  setSelectedOrigin: (org: string) => void;
  sortBy: string;
  setSortBy: (sort: string) => void;
  appLanguage: "fa" | "en";
  setAppLanguage: (lang: "fa" | "en") => void;
}

export default function Header({
  currentTab,
  setCurrentTab,
  settings,
  quoteCart,
  removeFromCart,
  updateCartItemNote,
  updateCartItemQuantity,
  onSubmitQuote,
  inquirySuccess,
  searchQuery,
  setSearchQuery,
  isAuthenticated,
  onLogout,
  selectedCategory,
  setSelectedCategory,
  selectedOrigin,
  setSelectedOrigin,
  sortBy,
  setSortBy,
  appLanguage,
  setAppLanguage,
}: HeaderProps) {
  const [isCartOpen, setIsCartOpen] = React.useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = React.useState(false);
  const [customerInfo, setCustomerInfo] = React.useState({ name: "", phone: "", company: "", note: "" });
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = React.useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  
  const appMode = import.meta.env.VITE_APP_MODE || "hybrid";
  const isAlmas = import.meta.env.VITE_TENANT_ID === "almas";

  // State-derived helpers
  const isAdminTab = currentTab === "admin";
  const isAlmasTheme = isAlmas && !isAdminTab;

  const formatPrice = (price: number) => {
    if (price === 0) return "تماس بگیرید";
    return `${price.toLocaleString()} ${settings.currency}`;
  };

  const cartTotal = quoteCart.reduce((acc, item) => acc + (item.price * item.quantity), 0);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerInfo.name || !customerInfo.phone) {
      alert("لطفاً نام و شماره تماس خود را وارد نمایید.");
      return;
    }
    onSubmitQuote({
      name: customerInfo.name,
      phone: customerInfo.phone,
      company: customerInfo.company,
      note: customerInfo.note,
    });
    setCustomerInfo({ name: "", phone: "", company: "", note: "" });
  };

  const dynCategories = React.useMemo(() => {
    return settings.categories || [];
  }, [settings.categories]);

  return (
    <header className="w-full bg-white text-slate-800 border-b border-slate-200 sticky top-0 z-50 shadow-xs" style={{ direction: appLanguage === "fa" ? "rtl" : "ltr" }}>
      {/* Top Banner with phone and site dynamic status */}
      {appMode !== "admin" && !isAdminTab && (
        <div className={`hidden md:block ${isAlmasTheme ? "bg-[#0b1120] text-slate-400 px-4 py-2.5 text-[11px] border-b border-blue-900/50" : "bg-slate-50 text-slate-600 px-4 py-2.5 text-[11px] border-b border-slate-200"}`}>
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-2">
            {/* Announcement or critical notification */}
            <div className="flex items-center gap-3 text-center md:text-right">
              <span className={`inline-flex items-center gap-1.5 font-bold px-2 py-0.5 rounded border text-[9px] tracking-wide ${isAlmasTheme ? "text-cyan-400 bg-cyan-950/50 border-cyan-900/50" : "text-indigo-700 bg-indigo-50 border-indigo-100"}`}>
                <span className="flex h-1.5 w-1.5 relative">
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isAlmasTheme ? "bg-cyan-400" : "bg-indigo-400"}`}></span>
                  <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${isAlmasTheme ? "bg-cyan-500" : "bg-indigo-600"}`}></span>
                </span>
                {isAlmasTheme 
                  ? (appLanguage === "en" ? "Almas Announcement:" : "اطلاعیه الماس سروری:") 
                  : (appLanguage === "en" ? "Adel Announcement:" : "اطلاعیه پیام عادل:")}
              </span>
              <span className={isAlmasTheme ? "text-slate-300 font-medium" : "text-slate-600 font-medium"}>
                {appLanguage === "en" ? (settings.announcementEn || settings.announcement) : settings.announcement}
              </span>
            </div>

            {/* Phone contacts and Address */}
            <div className="flex items-center gap-4">
              <a href={`tel:${settings.contactPhone}`} className={`transition-colors flex items-center gap-1.5 font-mono text-xs font-semibold ${isAlmasTheme ? "text-slate-300 hover:text-cyan-400" : "text-slate-600 hover:text-indigo-600"}`}>
                <Phone className={`w-3.5 h-3.5 ${isAlmasTheme ? "text-cyan-500" : "text-indigo-600"}`} />
                <span>{settings.contactPhone}</span>
              </a>
              
              {/* WhatsApp Link - Icon & Number only */}
              {(settings.whatsappNumber || settings.contactPhone) && (
                <>
                  <span className={`hidden sm:inline ${isAlmasTheme ? "text-slate-600" : "text-slate-300"}`}>|</span>
                  <a href={`https://wa.me/${(settings.whatsappNumber || settings.contactPhone).replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className={`transition-colors flex items-center gap-1.5 font-mono text-xs font-semibold ${isAlmasTheme ? "text-slate-300 hover:text-emerald-400" : "text-slate-600 hover:text-emerald-600"}`}>
                    <svg className={`w-3.5 h-3.5 ${isAlmasTheme ? "text-emerald-400" : "text-emerald-500"}`} viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.422-.272.347-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                    <span>{settings.whatsappNumber || settings.contactPhone}</span>
                  </a>
                </>
              )}

              <span className={`hidden sm:inline ${isAlmasTheme ? "text-slate-600" : "text-slate-300"}`}>|</span>
              <span className={`hidden sm:flex items-center gap-1.5 ${isAlmasTheme ? "text-slate-200" : "text-slate-600"}`}>
                <MapPin className={`w-3.5 h-3.5 ${isAlmasTheme ? "text-slate-200" : "text-slate-500"}`} />
                <span className="truncate max-w-sm" dir={appLanguage === "en" ? "ltr" : "rtl"}>
                  {appLanguage === "en" ? (settings.addressEn || settings.address) : settings.address}
                </span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Main navigation Header Bar */}
      <div className="max-w-7xl mx-auto px-4 py-3.5 flex flex-col sm:flex-row justify-between items-center gap-4">
        
        {/* Right Section: Logo & Nav */}
        <div className="flex flex-col sm:flex-row items-center gap-6 lg:gap-10">
          {/* Company Title and dynamic visual branding */}
          <div className="flex items-center gap-3 cursor-pointer select-none group" onClick={() => appMode !== "admin" && setCurrentTab("home")}>
            {(appMode === "admin" || isAdminTab) ? (
              <div className="h-12 md:h-14 w-auto min-w-[3rem] px-2 max-w-[120px] md:max-w-[160px] flex items-center justify-center rounded-2xl bg-white border border-slate-200 shadow-xl group-hover:scale-105 transition-all duration-300">
                <img src="https://osymbnivgxwtxymgzvre.supabase.co/storage/v1/object/public/platform-assets/1777879491006.png" alt="AFCMS Logo" width="120" height="40" className="h-10 w-auto object-contain p-1" referrerPolicy="no-referrer" />
              </div>
            ) : settings.logoUrl ? (
              <div className={`h-12 md:h-14 w-auto min-w-[3rem] px-2 max-w-[120px] md:max-w-[160px] flex items-center justify-center rounded-2xl group-hover:scale-105 transition-all duration-300 border shadow-sm ${isAlmasTheme ? "bg-cyan-50/50 border-cyan-100" : "bg-indigo-50/50 border-indigo-100"}`}>
                <img src={settings.logoUrl} alt={settings.title} width="160" height="56" className="w-full h-full object-contain drop-shadow-sm p-1" referrerPolicy="no-referrer" />
              </div>
            ) : (
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform duration-200 ${isAlmasTheme ? "bg-gradient-to-tr from-cyan-500 to-blue-600" : "bg-gradient-to-tr from-amber-500 to-indigo-600"}`}>
                {isAlmasTheme ? <Factory className="w-6 h-6 text-white" /> : <Palette className="w-6 h-6 text-white" />}
              </div>
            )}
            <div className="text-right" dir={appLanguage === "en" ? "ltr" : "rtl"} style={{ textAlign: appLanguage === "en" ? "left" : "right" }}>
              <h1 className={`text-base md:text-lg text-slate-900 font-black tracking-tight leading-none transition-colors ${isAdminTab ? "group-hover:text-indigo-600" : isAlmasTheme ? "group-hover:text-blue-600" : "group-hover:text-indigo-650"}`}>
                {(appMode === "admin" || isAdminTab) ? (appLanguage === "en" ? "AFCMS Central Management" : "سیستم مدیریت مرکزی (AFCMS)") : (appLanguage === "en" ? (settings.titleEn || settings.title) : settings.title)}
              </h1>
              <p className="text-[10px] text-slate-600 font-bold mt-1 uppercase tracking-widest leading-none">
                {(appMode === "admin" || isAdminTab) ? "MASTER CONTROL PORTAL" : (appLanguage === "en" ? (settings.subtitleEn || settings.subtitle || "Premium Industrial Catalog") : (settings.subtitle || "Premium Industrial Catalog"))}
              </p>
            </div>
          </div>

          {/* Desktop Navigation Links */}
          {appMode !== "admin" && !isAdminTab && (
            <nav className="hidden sm:flex items-center gap-1.5 md:gap-3">
              <button onClick={() => setCurrentTab("home")} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-black transition-all duration-300 group/btn cursor-pointer ${currentTab === "home" ? (isAlmasTheme ? "bg-blue-600 text-white shadow-lg shadow-blue-200" : "bg-indigo-600 text-white shadow-lg shadow-indigo-200") : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
                 <Home className={`w-4 h-4 transition-transform group-hover/btn:scale-110 ${currentTab === "home" ? "text-white" : (isAlmasTheme ? "text-blue-500" : "text-indigo-500")}`} />
                 {appLanguage === "en" ? "Home" : "صفحه اصلی"}
              </button>
              <button onClick={() => setCurrentTab("store")} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-black transition-all duration-300 group/btn cursor-pointer ${currentTab === "store" ? (isAlmasTheme ? "bg-blue-600 text-white shadow-lg shadow-blue-200" : "bg-indigo-600 text-white shadow-lg shadow-indigo-200") : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
                 <Store className={`w-4 h-4 transition-transform group-hover/btn:scale-110 ${currentTab === "store" ? "text-white" : (isAlmasTheme ? "text-blue-500" : "text-indigo-500")}`} />
                 {appLanguage === "en" ? "Products" : "لیست محصولات"}
              </button>
              <button onClick={() => setCurrentTab("blog")} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-black transition-all duration-300 group/btn cursor-pointer ${currentTab === "blog" ? (isAlmasTheme ? "bg-blue-600 text-white shadow-lg shadow-blue-200" : "bg-indigo-600 text-white shadow-lg shadow-indigo-200") : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
                 <MessageSquare className={`w-4 h-4 transition-transform group-hover/btn:scale-110 ${currentTab === "blog" ? "text-white" : (isAlmasTheme ? "text-blue-500" : "text-indigo-500")}`} />
                 {appLanguage === "en" ? "Blog" : "وبلاگ"}
              </button>
              <button onClick={() => setCurrentTab("about")} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-black transition-all duration-300 group/btn cursor-pointer ${currentTab === "about" ? (isAlmasTheme ? "bg-blue-600 text-white shadow-lg shadow-blue-200" : "bg-indigo-600 text-white shadow-lg shadow-indigo-200") : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
                 <Info className={`w-4 h-4 transition-transform group-hover/btn:scale-110 ${currentTab === "about" ? "text-white" : (isAlmasTheme ? "text-blue-500" : "text-indigo-500")}`} />
                 {appLanguage === "en" ? "About" : "درباره ما"}
              </button>
              <button onClick={() => setCurrentTab("contact")} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-black transition-all duration-300 group/btn cursor-pointer ${currentTab === "contact" ? (isAlmasTheme ? "bg-blue-600 text-white shadow-lg shadow-blue-200" : "bg-indigo-600 text-white shadow-lg shadow-indigo-200") : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
                 <Phone className={`w-4 h-4 transition-transform group-hover/btn:scale-110 ${currentTab === "contact" ? "text-white" : (isAlmasTheme ? "text-blue-500" : "text-indigo-500")}`} />
                 {appLanguage === "en" ? "Contact" : "ارتباط با ما"}
              </button>
            </nav>
          )}
        </div>

        {/* Left Section: Admin Dash, Inquiry Cart & Mobile Menu */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Mobile Menu Trigger - Now on the far left */}
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors sm:hidden cursor-pointer flex items-center gap-2"
          >
            <span className="text-[10px] font-black">{isMobileMenuOpen ? (appLanguage === "en" ? "CLOSE" : "بستن") : (appLanguage === "en" ? "MENU" : "منو")}</span>
            {isMobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>

          {/* Multi-Language Dropdown Switcher - Always Visible */}
          {settings.enableBilingual && (
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                {appLanguage === "fa" ? "زبان:" : "Language:"}
              </span>
              <div className="relative group/lang">
                <button 
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  title={appLanguage === "fa" ? "تغییر زبان" : "Change Language"}
                  className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2 rounded-xl text-[10px] sm:text-[11px] font-black transition-all duration-300 border border-slate-100 hover:bg-slate-50 cursor-pointer ${isLangMenuOpen ? "bg-slate-50 ring-2 ring-indigo-50" : ""}`}
                >
                  <div className="w-5 h-5 rounded-full overflow-hidden border border-slate-200 shadow-sm shrink-0">
                    {appLanguage === "fa" ? (
                      <img src="https://flagcdn.com/w40/af.png" className="w-full h-full object-cover scale-110" alt="AF" />
                    ) : (
                      <img src="https://flagcdn.com/w40/us.png" className="w-full h-full object-cover scale-110" alt="US" />
                    )}
                  </div>
                  <span className="text-slate-600">{appLanguage === "fa" ? "FA" : "EN"}</span>
                  <ChevronDown className={`w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-400 transition-transform duration-300 ${isLangMenuOpen ? "rotate-180" : ""}`} />
                </button>

                {isLangMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsLangMenuOpen(false)}></div>
                    <div className={`absolute top-full mt-2 ${appLanguage === "fa" ? "left-0" : "right-0"} w-36 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 p-1.5 animate-fadeIn`}>
                      <button 
                        onClick={() => { setAppLanguage("fa"); setIsLangMenuOpen(false); }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer ${appLanguage === "fa" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-50"}`}
                      >
                        <div className="w-5 h-5 rounded-full overflow-hidden border border-slate-100 shadow-xs">
                          <img src="https://flagcdn.com/w40/af.png" className="w-full h-full object-cover" alt="FA" />
                        </div>
                        <span className="text-xs font-bold">فارسی</span>
                      </button>
                      <button 
                        onClick={() => { setAppLanguage("en"); setIsLangMenuOpen(false); }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all mt-0.5 cursor-pointer ${appLanguage === "en" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-50"}`}
                      >
                        <div className="w-5 h-5 rounded-full overflow-hidden border border-slate-100 shadow-xs">
                          <img src="https://flagcdn.com/w40/us.png" className="w-full h-full object-cover" alt="EN" />
                        </div>
                        <span className="text-xs font-bold">English</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {isAuthenticated && appMode !== "client" && (
            <div className="relative">
              <button
                onClick={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
                className="px-3 md:px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-700 text-xs font-bold rounded-xl text-white shadow-xs transition-all flex items-center gap-2 cursor-pointer"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">پیشخوان مدیریت</span>
              </button>
              {isAdminMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsAdminMenuOpen(false)}></div>
                  <div className="absolute top-full mt-2 left-0 w-48 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-1.5 animate-fadeIn text-right" style={{ direction: "rtl" }}>
                    <button onClick={() => { window.location.href = "/app?tab=dashboard"; }} className="w-full text-right px-3 py-2.5 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 rounded-lg text-xs font-bold transition-colors flex items-center gap-2 cursor-pointer">
                      <LayoutDashboard className="w-4 h-4" /> داشبورد ادمین
                    </button>
                    <button onClick={() => { window.location.href = "/app?tab=products"; }} className="w-full text-right px-3 py-2.5 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 rounded-lg text-xs font-bold transition-colors flex items-center gap-2 mt-0.5 cursor-pointer">
                      <Database className="w-4 h-4" /> مدیریت کالاها
                    </button>
                    <button onClick={() => { window.location.href = "/app?tab=inquiries"; }} className="w-full text-right px-3 py-2.5 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 rounded-lg text-xs font-bold transition-colors flex items-center gap-2 mt-0.5 cursor-pointer">
                      <MessageSquare className="w-4 h-4" /> استعلام‌ها
                    </button>
                    <button onClick={() => { window.location.href = "/app?tab=settings"; }} className="w-full text-right px-3 py-2.5 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 rounded-lg text-xs font-bold transition-colors flex items-center gap-2 mt-0.5 cursor-pointer">
                      <Settings className="w-4 h-4" /> تنظیمات سیستم
                    </button>
                    <div className="w-full h-px bg-slate-100 my-1"></div>
                    <button onClick={() => { onLogout(); setIsAdminMenuOpen(false); }} className="w-full text-right px-3 py-2.5 hover:bg-red-50 text-red-600 rounded-lg text-xs font-bold transition-colors flex items-center gap-2 cursor-pointer">
                      <LogOut className="w-4 h-4" /> خروج از حساب
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Inquiry Cart Trigger (Client Mode Only) */}
          {appMode !== "admin" && !isAdminTab && settings.enableQuoteCart !== false && (
            <button
              onClick={() => setIsCartOpen(true)}
              className={`relative px-2.5 sm:px-4 py-2 rounded-xl transition-all duration-300 group cursor-pointer flex items-center gap-1.5 ${isAlmasTheme ? "bg-blue-600 text-white shadow-lg shadow-blue-200 hover:bg-blue-500" : "bg-indigo-600 text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"}`}
            >
              <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
              <span className="text-[10px] sm:text-xs font-black">{appLanguage === "en" ? "Inquiry" : "استعلام"}</span>
              {quoteCart.length > 0 && (
                <span className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-rose-500 text-white text-[10px] font-black rounded-full flex items-center justify-center border-2 border-white animate-bounce shadow-sm">
                  {quoteCart.length}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      {isMobileMenuOpen && (
        <div className="sm:hidden bg-white border-t border-slate-100 animate-slideDown overflow-hidden pb-4">
          <nav className="flex flex-col p-4 gap-2">
            <button onClick={() => { setCurrentTab("home"); setIsMobileMenuOpen(false); }} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-black transition-all ${currentTab === "home" ? (isAlmasTheme ? "bg-blue-600 text-white" : "bg-indigo-600 text-white") : "text-slate-600 hover:bg-slate-50"}`}>
               <Home className="w-4 h-4" />
               {appLanguage === "en" ? "Home" : "صفحه اصلی"}
            </button>
            <button onClick={() => { setCurrentTab("store"); setIsMobileMenuOpen(false); }} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-black transition-all ${currentTab === "store" ? (isAlmasTheme ? "bg-blue-600 text-white" : "bg-indigo-600 text-white") : "text-slate-600 hover:bg-slate-50"}`}>
               <Store className="w-4 h-4" />
               {appLanguage === "en" ? "Products" : "لیست محصولات"}
            </button>
            <button onClick={() => { setCurrentTab("blog"); setIsMobileMenuOpen(false); }} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-black transition-all ${currentTab === "blog" ? (isAlmasTheme ? "bg-blue-600 text-white" : "bg-indigo-600 text-white") : "text-slate-600 hover:bg-slate-50"}`}>
               <MessageSquare className="w-4 h-4" />
               {appLanguage === "en" ? "Blog" : "وبلاگ"}
            </button>
            <button onClick={() => { setCurrentTab("about"); setIsMobileMenuOpen(false); }} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-black transition-all ${currentTab === "about" ? (isAlmasTheme ? "bg-blue-600 text-white" : "bg-indigo-600 text-white") : "text-slate-600 hover:bg-slate-50"}`}>
               <Info className="w-4 h-4" />
               {appLanguage === "en" ? "About" : "درباره ما"}
            </button>
            <button onClick={() => { setCurrentTab("contact"); setIsMobileMenuOpen(false); }} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-black transition-all ${currentTab === "contact" ? (isAlmasTheme ? "bg-blue-600 text-white" : "bg-indigo-600 text-white") : "text-slate-600 hover:bg-slate-50"}`}>
               <Phone className="w-4 h-4" />
               {appLanguage === "en" ? "Contact" : "ارتباط با ما"}
            </button>
            
            {settings.enableQuoteCart !== false && (
              <button onClick={() => { setIsCartOpen(true); setIsMobileMenuOpen(false); }} className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-sm font-black transition-all text-emerald-600 bg-emerald-50 hover:bg-emerald-100 mt-2">
                <div className="flex items-center gap-3">
                  <ClipboardList className="w-4 h-4" />
                  <span>{appLanguage === "en" ? "My Inquiries" : "لیست استعلام من"}</span>
                </div>
                {quoteCart.length > 0 && (
                  <span className="bg-emerald-600 text-white text-[10px] px-2 py-0.5 rounded-full shadow-sm">{quoteCart.length} مورد</span>
                )}
              </button>
            )}
          </nav>
        </div>
      )}

      {/* Dynamic Categories Dropdown Menu & Filters - ONLY VISIBLE IN STORE MODE */}
      {appMode !== "admin" && currentTab === "store" && (
        <div className="border-t border-slate-100 bg-white relative z-30 shadow-sm animate-slideDown">
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex flex-col md:flex-row items-center gap-3">
            
            {/* Search Bar - Full width on mobile, compact on desktop */}
            <div className="relative w-full md:max-w-xs">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder={appLanguage === "en" ? "Search products..." : "جستجوی هوشمند کالا..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pr-10 pl-4 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs md:text-sm focus:outline-none focus:ring-2 transition-all shadow-inner ${isAlmasTheme ? "focus:ring-blue-500/20 focus:border-blue-400" : "focus:ring-indigo-500/20 focus:border-indigo-400"}`}
                dir={appLanguage === "en" ? "ltr" : "rtl"}
              />
            </div>
  
            {/* Advanced Filters Row - Horizontally scrollable on mobile */}
            <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto no-scrollbar pb-1 md:pb-0 scroll-smooth">
              {/* Category Selector */}
              <div className="flex items-center gap-1.5 shrink-0">
                <div className={`hidden md:flex p-2 rounded-lg ${isAlmasTheme ? "bg-blue-50 text-blue-600" : "bg-indigo-50 text-indigo-600"}`}>
                  <Filter className="w-3.5 h-3.5" />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => { setSelectedCategory(e.target.value); setCurrentTab("store"); }}
                  className={`min-w-[120px] md:w-40 text-[11px] md:text-xs px-3 py-2 bg-white border border-slate-200 focus:outline-none rounded-xl text-slate-700 font-bold cursor-pointer shadow-sm hover:border-slate-300 transition-colors ${isAlmasTheme ? "focus:border-blue-400" : "focus:border-indigo-400"}`}
                  dir={appLanguage === "en" ? "ltr" : "rtl"}
                >
                  <option value="همه">{appLanguage === "en" ? "All Categories" : "همه دسته‌ها"}</option>
                  {dynCategories.map(cat => <option key={cat} value={cat}>{appLanguage === "en" ? (settings.categoriesEn?.[cat] || cat) : cat}</option>)}
                </select>
              </div>

              {/* Origin Selector */}
              <select
                value={selectedOrigin}
                onChange={(e) => { setSelectedOrigin(e.target.value); setCurrentTab("store"); }}
                className={`min-w-[110px] md:w-36 text-[11px] md:text-xs px-3 py-2 bg-white border border-slate-200 focus:outline-none rounded-xl text-slate-700 font-bold cursor-pointer shadow-sm hover:border-slate-300 transition-colors shrink-0 ${isAlmasTheme ? "focus:border-blue-400" : "focus:border-indigo-400"}`}
                dir={appLanguage === "en" ? "ltr" : "rtl"}
              >
                <option value="همه">{appLanguage === "en" ? "All Origins" : "همه کشورها"}</option>
                {(settings.origins || ["ایران", "آلمان", "چین", "ایتالیا", "هند", "ترکیه"]).map(org => (
                  <option key={org} value={org}>{appLanguage === "en" ? (settings.originsEn?.[org] || org) : `تولید ${org}`}</option>
                ))}
              </select>
  
              {/* Sort Selector */}
              <select
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); setCurrentTab("store"); }}
                className={`min-w-[120px] md:w-40 text-[11px] md:text-xs px-3 py-2 bg-white border border-slate-200 focus:outline-none rounded-xl text-slate-700 font-bold cursor-pointer shadow-sm hover:border-slate-300 transition-colors shrink-0 ${isAlmasTheme ? "focus:border-blue-400" : "focus:border-indigo-400"}`}
                dir={appLanguage === "en" ? "ltr" : "rtl"}
              >
                <option value="default">{appLanguage === "en" ? "Sort: Default" : "ترتیب: پیش‌فرض"}</option>
                <option value="price-desc">{appLanguage === "en" ? "Highest Price" : "گران‌ترین"}</option>
                <option value="price-asc">{appLanguage === "en" ? "Lowest Price" : "ارزان‌ترین"}</option>
                <option value="name-asc">{appLanguage === "en" ? "Name (A-Z)" : "نام (الفبا)"}</option>
              </select>
  
              {/* Reset Button - Only shows when filters are active */}
              {(selectedCategory !== "همه" || selectedOrigin !== "همه" || sortBy !== "default" || searchQuery !== "") && (
                <button
                  onClick={() => {
                    setSelectedCategory("همه");
                    setSelectedOrigin("همه");
                    setSortBy("default");
                    setSearchQuery("");
                    setCurrentTab("store");
                  }}
                  className="px-4 py-2 bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-100 rounded-xl text-[11px] font-bold transition-all cursor-pointer shrink-0 flex items-center gap-1.5 shadow-sm active:scale-95"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span className="whitespace-nowrap">{appLanguage === "en" ? "Reset" : "حذف فیلتر"}</span>
                </button>
              )}
            </div>
  
          </div>
        </div>
      )}

      {/* Inquiry Cart Drawer / Sidebar */}
      {isCartOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end animate-fadeIn">
          {/* Overlay */}
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs" onClick={() => setIsCartOpen(false)}></div>
          
          {/* Drawer Panel */}
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col animate-slideLeft" style={{ direction: appLanguage === "fa" ? "rtl" : "ltr" }}>
            {/* Drawer Header */}
            <div className={`p-6 border-b flex items-center justify-between ${isAlmasTheme ? "bg-blue-600 text-white" : "bg-indigo-600 text-white"}`}>
              <div className="flex items-center gap-3">
                <ClipboardList className="w-6 h-6" />
                <div>
                  <h3 className="text-lg font-black">{appLanguage === "en" ? "Inquiry List" : "لیست استعلام قیمت"}</h3>
                  <p className="text-[10px] opacity-80 font-bold">{appLanguage === "en" ? `${quoteCart.length} items in list` : `${quoteCart.length} کالا در لیست انتظار`}</p>
                </div>
              </div>
              <button onClick={() => setIsCartOpen(false)} className="p-2 hover:bg-white/20 rounded-xl transition-colors cursor-pointer">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Cart Items or Success State */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {inquirySuccess ? (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center animate-fadeIn">
                  <div className="w-24 h-24 bg-emerald-50 text-emerald-500 rounded-[2.5rem] flex items-center justify-center mb-8 shadow-inner border border-emerald-100 rotate-6">
                    <CheckCircle className="w-12 h-12" />
                  </div>
                  <h3 className="text-2xl font-black text-slate-900 mb-4">{appLanguage === "en" ? "Successfully Submitted" : "ارسال موفقیت‌آمیز"}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed font-medium mb-10 max-w-xs mx-auto">
                    {appLanguage === "en" ? "Your inquiry has been successfully registered in our system. Our experts will contact you for final coordination within the next 24 hours." : "درخواست استعلام شما با موفقیت در سیستم ثبت گردید. کارشناسان ما حداکثر تا ۲۴ ساعت آینده جهت هماهنگی نهایی با شما تماس خواهند گرفت."}
                  </p>
                  <button 
                    onClick={() => setIsCartOpen(false)} 
                    className="px-10 py-4 bg-slate-900 text-white rounded-2xl font-black text-sm shadow-xl shadow-slate-200 transition-all hover:bg-slate-800 active:scale-95 cursor-pointer"
                  >
                    {appLanguage === "en" ? "Close Window" : "متوجه شدم، بستن پنجره"}
                  </button>
                </div>
              ) : quoteCart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center space-y-4">
                  <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center border border-slate-100">
                    <ClipboardList className="w-10 h-10 text-slate-200" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-slate-500 font-bold">{appLanguage === "en" ? "Your list is empty" : "لیست استعلام شما خالی است"}</p>
                    <p className="text-[10px] text-slate-500">{appLanguage === "en" ? "Add items from the catalog to this list." : "کالاهای مورد نظر خود را از فروشگاه به این لیست اضافه کنید."}</p>
                  </div>
                  <button onClick={() => { setCurrentTab("store"); setIsCartOpen(false); }} className="px-6 py-3 bg-slate-900 text-white rounded-xl text-xs font-bold shadow-md hover:bg-slate-800 transition-all cursor-pointer">{appLanguage === "en" ? "View Products" : "مشاهده محصولات"}</button>
                </div>
              ) : (
                <div className="p-6 space-y-4">
                  {quoteCart.map((item) => (
                    <div key={item.id} className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3 relative group overflow-hidden">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1">
                          <h4 className="text-xs font-black text-slate-800 leading-tight">{item.name}</h4>
                          <p className="text-[10px] text-slate-500 font-bold mt-1 uppercase">{item.sku} • {item.category}</p>
                        </div>
                        <button onClick={() => removeFromCart(item.id)} className="text-rose-400 hover:text-rose-600 transition-colors p-1 cursor-pointer">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-1 shadow-sm">
                          <button onClick={() => updateCartItemQuantity(item.id, Math.max(1, item.quantity - 1))} className="p-1 hover:bg-slate-100 rounded text-slate-500 cursor-pointer"><Minus className="w-3.5 h-3.5"/></button>
                          <span className="text-xs font-black text-slate-800 min-w-[20px] text-center">{item.quantity}</span>
                          <button onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)} className="p-1 hover:bg-slate-100 rounded text-slate-500 cursor-pointer"><Plus className="w-3.5 h-3.5"/></button>
                        </div>
                        <div className="text-left font-black text-indigo-700 text-xs font-mono">
                          {formatPrice(item.price * item.quantity)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Inquiry Form */}
            {!inquirySuccess && quoteCart.length > 0 && (
              <div className="p-6 border-t bg-slate-50/50 space-y-4">
                <div className="flex justify-between items-center mb-2 px-1">
                  <span className="text-xs text-slate-500 font-bold">{appLanguage === "en" ? "Estimated Total:" : "جمع برآوردی لیست:"}</span>
                  <span className="text-lg font-black text-slate-900 font-mono">{formatPrice(cartTotal)}</span>
                </div>

                <form onSubmit={handleFormSubmit} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder={appLanguage === "en" ? "Full Name *" : "نام و نام‌خانوادگی *"}
                      required
                      value={customerInfo.name}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                      className={`w-full text-xs px-4 py-3 bg-white border border-slate-200 rounded-xl focus:border-indigo-500 focus:outline-none shadow-sm font-bold ${appLanguage === "en" ? "text-left" : "text-right"}`}
                    />
                    <input
                      type="tel"
                      placeholder={appLanguage === "en" ? "Phone Number *" : "شماره تماس *"}
                      required
                      value={customerInfo.phone}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                      className="w-full text-xs px-4 py-3 bg-white border border-slate-200 rounded-xl focus:border-indigo-500 focus:outline-none shadow-sm font-bold text-left"
                      dir="ltr"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder={appLanguage === "en" ? "Company / Project (Optional)" : "نام شرکت / پروژه (اختیاری)"}
                    value={customerInfo.company}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, company: e.target.value })}
                    className={`w-full text-xs px-4 py-3 bg-white border border-slate-200 rounded-xl focus:border-indigo-500 focus:outline-none shadow-sm font-bold ${appLanguage === "en" ? "text-left" : "text-right"}`}
                  />
                  <textarea
                    placeholder={appLanguage === "en" ? "Additional Notes..." : "توضیحات تکمیلی..."}
                    rows={2}
                    value={customerInfo.note}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, note: e.target.value })}
                    className={`w-full text-xs px-4 py-3 bg-white border border-slate-200 rounded-xl focus:border-indigo-500 focus:outline-none shadow-sm font-medium leading-relaxed ${appLanguage === "en" ? "text-left" : "text-right"}`}
                  />
                  
                  <button
                    type="submit"
                    className={`w-full py-4 rounded-2xl font-black text-sm text-white shadow-xl shadow-indigo-200 transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-3 ${isAlmasTheme ? "bg-blue-600 hover:bg-blue-500" : "bg-indigo-600 hover:bg-indigo-500"}`}
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span>{appLanguage === "en" ? "Send Inquiry" : "ارسال نهایی استعلام"}</span>
                  </button>
                </form>
                
                {inquirySuccess && (
                  <div className="bg-emerald-50 text-emerald-700 p-3 rounded-xl border border-emerald-100 text-center text-[10px] font-black animate-fadeIn">
                    درخواست شما با موفقیت ثبت شد. کارشناسان ما به زودی تماس می‌گیرند.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
